import type {
  BaseEntity,
  Resource,
  ResourceType,
  Role,
  UserDetail,
  UserType,
} from '~settings/api/lib/schemas';
import type { UserDetailPayload } from '~settings/api/lib/types';
import { fromDropdownList, toDropdownList } from '~settings/utils/transformers';

import type { UserFormSchema } from '../schema';

function fromListToResources(
  resourceType: ResourceType,
  resources: { value: string }[],
): Resource[] {
  return resources.map((resource) => ({
    id: resource.value,
    type: resourceType,
  }));
}

export const mapFormDataToApi = (
  data: UserFormSchema,
  isInternalUpdate = true,
): UserDetailPayload => {
  const baseInfo = {
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone?.length ? data.phone : null,
    jobTitle: data.jobTitle,
    organization: data.organization ?? '',
  };

  if (isInternalUpdate) {
    const roleResources: UserDetailPayload['roleResources'] = {};
    const isExternal = data.userType === 'EXTERNAL';
    const resourceType: ResourceType = isExternal ? 'LABEL' : 'RESOURCE_GROUP';

    for (const { role, resources } of data.roleMap ?? []) {
      const newResources = resources.filter(
        (label) =>
          !roleResources[role]?.find(
            (resource) =>
              resource.id === label.value && resource.type === resourceType,
          ),
      );

      roleResources[role] = (roleResources[role] ?? []).concat(
        ...fromListToResources(resourceType, newResources),
      );
    }

    return {
      ...baseInfo,
      userType: data.userType,
      isActive: data.isActive,
      roleResources,
    };
  }

  const rolesMap: Partial<UserDetailPayload['rolesMap']> = {};

  if (data.internalPermissions) {
    for (const role of data.internalPermissions.roles ?? []) {
      rolesMap[role.value as Role] = {
        labels: [],
        labelGroups: fromDropdownList(
          data.internalPermissions.labelGroups ?? [],
        ),
      };
    }
  } else {
    for (const { role, labels } of data.externalPermissions ?? []) {
      const existingRole = rolesMap[role];

      if (existingRole) {
        const uniqueLabels = labels.filter(
          (label) =>
            !existingRole.labels.find(
              (storedLabel: BaseEntity) => storedLabel.id === label.value,
            ),
        );

        rolesMap[role] = {
          labels: [...existingRole.labels, ...fromDropdownList(uniqueLabels)],
          labelGroups: [],
        };
      } else {
        rolesMap[role] = {
          labels: fromDropdownList(labels),
          labelGroups: [],
        };
      }
    }
  }

  return {
    firstName: data.firstName,
    lastName: data.lastName,
    isInternal: data.userType === 'INTERNAL',
    email: data.email,
    phone: data.phone?.length ? data.phone : null,
    isActive: data.isActive,
    jobTitle: data.jobTitle,
    organization: data.organization ?? '',
    rolesMap: rolesMap as UserDetailPayload['rolesMap'],
  };
};

function convertUserType(user: UserDetail): UserType {
  if (user.userType) {
    return user.userType;
  }

  if (!user.isInternal) {
    return 'EXTERNAL';
  }

  const roles = Object.keys(user.rolesMap ?? {}) as Role[];

  if (roles.includes('TECH_ADMIN')) {
    return 'SUPERADMIN';
  }

  if (roles.includes('SUPPORT_ADMIN')) {
    return 'SUPERADMIN';
  }

  if (roles.includes('OPS_ADMIN')) {
    return 'ADMIN';
  }

  if (roles.includes('RELEASE_MANAGER')) {
    return 'MANAGER';
  }

  if (roles.includes('VIDEO_MANAGER')) {
    return 'MANAGER';
  }

  return 'VIEWER';
}

const oldToNew: Record<Role, (userType?: UserType) => Role[]> = {
  TECH_ADMIN: () => ['RELEASE_APPROVER', 'VIDEO_APPROVER', 'REPORTER'],
  SUPPORT_ADMIN: () => ['RELEASE_APPROVER', 'VIDEO_APPROVER', 'REPORTER'],
  OPS_ADMIN: () => ['RELEASE_APPROVER', 'VIDEO_COORDINATOR', 'REPORTER'],
  RELEASE_MANAGER: (userType) =>
    userType
      ? ['RELEASE_MANAGER']
      : ['RELEASE_APPROVER', 'VIDEO_COORDINATOR', 'REPORTER'],
  VIDEO_MANAGER: () => ['VIDEO_APPROVER', 'REPORTER'],
  RELEASE_APPROVER: () => ['RELEASE_APPROVER'],
  VIDEO_APPROVER: () => ['VIDEO_APPROVER'],
  REPORTER: () => ['REPORTER'],
  INVENTORY_MANAGER: () => ['INVENTORY_MANAGER'],
  LABEL_ADMIN: () => ['LABEL_ADMIN'],
  RELEASE_COORDINATOR: () => ['RELEASE_COORDINATOR'],
  VIDEO_COORDINATOR: () => ['VIDEO_COORDINATOR'],
};

function buildRoleMap(user: UserDetail): UserFormSchema['roleMap'] {
  const roleResources = new Map<
    Role,
    {
      role: Role;
      resources: { label: string; value: string }[];
      type: ResourceType;
    }[]
  >();

  if (user.roleResources) {
    for (const [oldRole, resources] of Object.entries(user.roleResources)) {
      const newRoles = oldToNew[oldRole as Role](user.userType);

      for (const newRole of newRoles) {
        if (!roleResources.has(newRole)) {
          roleResources.set(newRole, []);
        }

        roleResources.get(newRole)!.push({
          role: newRole,
          resources: toDropdownList(resources),
          type: resources[0].type,
        });
      }
    }
  } else if (user.rolesMap) {
    const type: ResourceType = user.isInternal ? 'RESOURCE_GROUP' : 'LABEL';
    const rolesMap = user.rolesMap ?? {};

    for (const [oldRole, { labels, labelGroups }] of Object.entries(rolesMap)) {
      const newRoles = oldToNew[oldRole as Role](user.userType);

      for (const newRole of newRoles) {
        if (!roleResources.has(newRole)) {
          roleResources.set(newRole, []);
        }

        roleResources.get(newRole)!.push({
          role: newRole,
          resources: toDropdownList(user.isInternal ? labelGroups : labels),
          type,
        });
      }
    }
  }

  return Array.from(roleResources.values()).flat();
}

export const mapApiDataToForm = (
  user: UserDetail,
  isInternalUpdate = true,
): UserFormSchema => {
  if (isInternalUpdate) {
    return {
      userType: convertUserType(user),
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      jobTitle: user.jobTitle,
      phone: user.phone ?? '',
      isActive: user.isActive ?? true,
      organization: user.organization ?? '',
      roleMap: buildRoleMap(user),
    };
  }

  const rolesMap = user.rolesMap ?? {};

  if (user.isInternal) {
    const userRoles = Object.keys(rolesMap) as Role[];
    const firstRole = rolesMap[userRoles[0]]; // they are the same for all internal roles
    const labelGroups: UserDetail['labelGroups'] = firstRole?.labelGroups ?? [];

    return {
      externalPermissions: [],
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      jobTitle: user.jobTitle,
      phone: user.phone ?? '',
      isActive: user.isActive ?? true,
      organization: user.organization ?? '',
      userType: 'INTERNAL',
      internalPermissions: {
        roles: userRoles.map((role) => ({
          label: role,
          value: role,
        })),
        labelGroups: toDropdownList(labelGroups) ?? [],
      },
    };
  }

  const externalPermissions = Object.entries(rolesMap).map(
    ([role, entries]) => {
      return {
        role: role as Role,
        labels: toDropdownList(entries.labels) ?? [],
      };
    },
  );

  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    jobTitle: user.jobTitle,
    phone: user.phone ?? '',
    isActive: user.isActive ?? true,
    userType: 'EXTERNAL',
    externalPermissions,
  };
};
